import React from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FilterListIcon from '@material-ui/icons/FilterList';
import TextField from '@material-ui/core/TextField';
import queryString from 'query-string';
import useRouter from 'use-react-router';
import { MTableToolbar } from 'material-table';
import { t } from 'ttag';
import { useApolloClient } from '@apollo/react-hooks';
import { DatePicker } from '@material-ui/pickers';
import DataTable from '../../../components/DataTable';
import useUser from '../../../auth/hooks';
import useApi, { MyClubCApi } from '../../../api';
import {
  getCurrentYear,
  insertAt,
  nexYear,
  prepareParam,
} from '../../../utils/helpers';
import { useNavigator } from '../../../components/Navigator';
import {
  isManager,
  isSuperManager,
  isTrainer,
} from '../../../utils/permissions';
import MyChip from '../../../components/MyChip';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import { USER_STATUS } from '../../../utils/enums';
import Link from '@material-ui/core/Link';

const ListView = () => {
  const { user } = useUser();
  const api = useApi();
  const client = useApolloClient();
  const newApi = new MyClubCApi(client);
  const navigate = useNavigator();
  const [loading, setLoading] = React.useState(false);
  const [trainers, setTrainers] = React.useState([]);
  const [filterTrainers, setFilterTrainers] = React.useState([]);
  const [gyms, setGyms] = React.useState([]);
  const { history } = useRouter();
  const searchParams = new URLSearchParams(history.location.search);
  const [pageState, setPageState] = React.useState({
    gymId: parseInt(searchParams.get('gymId'), 10) || undefined,
    trainerId: parseInt(searchParams.get('trainerId'), 10) || undefined,
    year: searchParams.get('year') || String(getCurrentYear()),
  });
  if (user.isAdmin || isSuperManager(user) || isManager(user)) {
    //pass
  } else if (isTrainer(user)) {
    navigate(api.router.contributions.athleteList(user.id));
  } else {
    throw new Error('Access error');
  }

  const updateHistory = (searchParams) => {
    history.replace({
      search: `?${queryString.stringify(searchParams)}`,
    });
  };

  const fetchPageData = async () => {
    setLoading(true);
    updateHistory(prepareParam(pageState));
    const contributionTrainers = await newApi.getContributionTrainers({
      year: pageState.year,
      gymId: pageState.gymId,
      trainerId: pageState.trainerId,
    });
    setTrainers(contributionTrainers);
    setLoading(false);
  };

  React.useEffect(() => {
    fetchPageData();
  }, [pageState]);

  React.useEffect(() => {
    async function fetchData() {
      const gyms = await newApi.getGyms();
      const trainersData = await newApi.getTrainers({
        status: USER_STATUS.active,
      });
      setFilterTrainers(trainersData);
      setGyms(gyms);
    }
    fetchData();
  }, []);

  const handleChangePage = (name, value) => {
    setPageState({ ...pageState, [name]: value });
  };

  const onChangeYear = async (e) => {
    handleChangePage('year', String(e.getFullYear()));
  };

  const columns = [
    {
      title: t`Id`,
      field: 'id',
      editable: 'never',
      filtering: false,
      width: 4,
    },
    {
      title: t`Тренер`,
      field: 'fullName',
      filtering: false,
      render: (d) => {
        return d.status === USER_STATUS.archived ? (
          <Tooltip placement='top' title={t`Архівний`}>
            <span>
              <Link target='_blank' href={api.router.trainers.edit(d.id)}>
                <span style={{ color: 'grey' }}>{d.fullName}</span>
              </Link>
            </span>
          </Tooltip>
        ) : (
          <Link target='_blank' href={api.router.trainers.edit(d.id)}>
            {d.fullName}
          </Link>
        );
      },
    },
    {
      title: t`Зал`,
      field: 'gyms',
      render: ({ id, contributionGyms, gyms }) => {
        const gIds = gyms.map((r) => r.id);
        let diff = contributionGyms.filter((g) => !gIds.includes(g.id));
        return gyms
          .map((r) => (
            <MyChip
              key={r.id}
              variant='outlined'
              color='#2196f3'
              size='small'
              label={r.name}
              onClick={() => {
                navigate(
                  `${api.router.contributions.athleteList(id)}?year=${
                    pageState.year
                  }&gymId=${r.id}`
                );
              }}
            />
          ))
          .concat(
            diff.map((r) => (
              <Tooltip key={r.id} title={t`Не тренує в цьму залі`}>
                <span>
                  <MyChip
                    color='grey'
                    key={r.id}
                    variant='outlined'
                    size='small'
                    label={r.name}
                    onClick={() => {
                      navigate(
                        `${api.router.contributions.athleteList(id)}?year=${
                          pageState.year
                        }&gymId=${r.id}`
                      );
                    }}
                  />
                </span>
              </Tooltip>
            ))
          );
      },
    },
    {
      title: t`Загальна кількість спортсменів`,
      field: 'athleteCount',
      filtering: false,
      width: 8,
      render: ({ athleteCount, athleteWithContributionCount }) => {
        return (
          <span>
            {`${athleteCount}/`}
            <span style={{ color: 'green' }}>
              {athleteWithContributionCount}
            </span>
          </span>
        );
      },
    },
    {
      title: t`Сплачено`,
      field: 'amount',

      render: (d) => (
        <MyChip
          label={d.amount || 0}
          color={(d.amount || 0) > 0 ? 'green' : 'red'}
          variant='outlined'
        />
      ),
    },
  ];
  const adminColumns = [
    {
      title: t`Клуб`,
      field: 'club',
      render: ({ club }) => club.shortName,
      filtering: false,
    },
  ];

  const NewTitle = () => (
    <h6
      className='MuiTypography-root MuiTypography-h6'
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {t`Членські внески`}
      <div style={{ fontWeight: 'normal', fontSize: '15px' }}>
        <span>
          {t`Всього спортсменів: `}
          <b>
            {trainers.map((r) => r.athleteCount).reduce((a, b) => a + b, 0)}
          </b>
        </span>
        <span>
          {t` Сума внесків: `}
          <b>
            {trainers
              .map((r) => r.amount)
              .reduce((a, b) => Number(a) + Number(b), 0)}
          </b>
        </span>
        <span>
          {t` Спортсменів які здали: `}
          <b>
            {trainers
              .map((r) => r.athleteWithContributionCount)
              .reduce((a, b) => a + b, 0)}
          </b>
        </span>
      </div>
      <div style={{ fontWeight: 'normal', fontSize: '15px' }}></div>
    </h6>
  );

  user.isAdmin && insertAt(columns, 1, ...adminColumns);
  return (
    <Paper>
      <DataTable
        isLoading={loading}
        title={<NewTitle />}
        columns={columns}
        data={trainers}
        customActions={[
          {
            icon: 'visibility',
            tooltip: t`Список спортсменів`,
            onClick: (event, rowData) => {
              navigate(
                `${api.router.contributions.athleteList(rowData.id)}?year=${
                  pageState.year
                }&gymId=${pageState.gymId}`
              );
            },
          },
        ]}
        components={{
          Toolbar: (props) => (
            <div>
              <MTableToolbar {...props} />
              <Grid
                container
                spacing={2}
                style={{ marginLeft: '10px', marginTop: '5px' }}
                justifyContent='flex-start'
                alignItems='center'
              >
                <Grid item>
                  <Tooltip title={t`Виберіть фільтр`}>
                    <FilterListIcon />
                  </Tooltip>
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={filterTrainers}
                    getOptionLabel={(option) => option.fullInitials}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Виберіть тренера`}
                        variant='outlined'
                      />
                    )}
                    value={filterTrainers.find(
                      (item) => item && item.id === pageState.trainerId
                    )}
                    onChange={(e, value) =>
                      handleChangePage('trainerId', value && value.id)
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={gyms}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Виберіть зал`}
                        variant='outlined'
                      />
                    )}
                    value={gyms.find((g) => g && g.id === pageState.gymId)}
                    onChange={(e, value) =>
                      handleChangePage('gymId', value && value.id)
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <DatePicker
                    size={'small'}
                    variant='inline'
                    label={t`Рік внеску`}
                    InputAdornmentProps={{ position: 'start' }}
                    inputVariant='outlined'
                    openTo='year'
                    format='yyyy'
                    views={['year']}
                    value={pageState.year}
                    cancelLabel='Відмінити'
                    maxDate={nexYear()}
                    onChange={onChangeYear}
                  />
                </Grid>
              </Grid>
            </div>
          ),
        }}
      />
    </Paper>
  );
};

export default ListView;
